import React, { useEffect, useRef, useState } from 'react';
import { useMedia } from '../../context/MediaContext';
import { verifyPhoto } from '../../../ai-interview/utils/aiInterviewApi';

/** Live photo capture: snapshot from the camera → preview → retake/confirm → store original. */
const PhotoCapture = ({ ids, onDone }) => {
  const { camera } = useMedia();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [shot, setShot] = useState(null);     // { url, blob }
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  // Re-attach the live stream whenever we're showing the camera (incl. after "Retake", when the
  // <video> element remounts) — `shot` is in the deps so the new element gets srcObject set again.
  useEffect(() => {
    if (!shot && videoRef.current && camera.stream) videoRef.current.srcObject = camera.stream;
  }, [camera.stream, shot]);

  const capture = () => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) return;
    const c = canvasRef.current;
    c.width = v.videoWidth; c.height = v.videoHeight;
    c.getContext('2d').drawImage(v, 0, 0, c.width, c.height);
    c.toBlob((blob) => { if (blob) setShot({ url: URL.createObjectURL(blob), blob }); }, 'image/jpeg', 0.9);
  };

  const confirm = async () => {
    if (!shot) return;
    setBusy(true); setErr('');
    try {
      await verifyPhoto(ids.companyId, ids.sessionId, ids.candidateId, shot.blob);
      onDone({ ok: true });
    } catch (e) { setErr(e.message || 'Could not save the photo.'); setBusy(false); }
  };

  return (
    <div>
      <div className="relative mx-auto aspect-video max-w-md overflow-hidden rounded-2xl bg-slate-100 ring-1 ring-slate-200 dark:bg-slate-800 dark:ring-white/10">
        {shot ? (
          <img src={shot.url} alt="Captured" className="h-full w-full object-cover [transform:scaleX(-1)]" />
        ) : (
          <video ref={videoRef} autoPlay playsInline muted className="h-full w-full object-cover [transform:scaleX(-1)]" />
        )}
      </div>
      <canvas ref={canvasRef} className="hidden" />
      {err && <p className="mt-2 text-center text-xs text-rose-500">{err}</p>}

      <div className="mt-5 flex justify-center gap-3">
        {!shot ? (
          <button type="button" onClick={capture}
            className="rounded-full bg-primary-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-primary-700 dark:bg-violet-600 dark:hover:bg-violet-500">
            📸 Capture photo
          </button>
        ) : (
          <>
            <button type="button" onClick={() => { if (shot) URL.revokeObjectURL(shot.url); setShot(null); setErr(''); }} disabled={busy}
              className="rounded-full border border-slate-300 px-5 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-50 dark:border-white/15 dark:text-slate-200 dark:hover:bg-white/5">
              Retake
            </button>
            <button type="button" onClick={confirm} disabled={busy}
              className="rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50">
              {busy ? 'Saving…' : 'Use this photo'}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default PhotoCapture;
